// frontend/src/pages/RestDayMonitoring/components/RestDayStatsCards.js
import React from 'react';
import { Users, AlertOctagon, Eye, ShieldCheck } from 'lucide-react';

/**
 * RestDayStatsCards - 4-card summary for 12-day rest day interval monitoring.
 * 
 * Cards:
 * 1. Total Monitored (blue) - all employees tracked
 * 2. Action Required (red) - critical + non-compliant
 * 3. Watch List (amber) - watch + warning
 * 4. Compliance Rate (green) - percentage compliant
 * 
 * Design: admin red theme per design-system-reference.md
 * Cards 1-3 are clickable and filter the table below.
 */
const RestDayStatsCards = ({ 
  stats, 
  loading,
  onCardClick,
  activeFilter = ''
}) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 animate-pulse">
            <div className="flex items-center">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0" />
              <div className="ml-2 sm:ml-4 flex-1 min-w-0">
                <div className="h-7 bg-gray-200 rounded w-16 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-24" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const criticalCount = stats.criticalCount || 0;
  const nonCompliantCount = stats.nonCompliantCount || 0;
  const watchCount = stats.watchCount || 0;
  const warningCount = stats.warningCount || 0;

  const actionRequiredCount = criticalCount + nonCompliantCount;
  const watchListCount = watchCount + warningCount;

  // Build breakdown strings
  const actionBreakdown = [
    nonCompliantCount > 0 && `${nonCompliantCount} violated`,
    criticalCount > 0 && `${criticalCount} critical`,
  ].filter(Boolean).join(' · ') || 'All clear';

  const watchBreakdown = [
    warningCount > 0 && `${warningCount} warning`,
    watchCount > 0 && `${watchCount} watch`,
  ].filter(Boolean).join(' · ') || 'All clear';

  const handleClick = (filterValue) => {
    if (!onCardClick) return;
    if (activeFilter === filterValue) {
      onCardClick('');
    } else {
      onCardClick(filterValue);
    }
  };

  const cards = [
    {
      id: 'total',
      filterValue: '',
      label: 'Employees Tracked',
      value: stats.totalMonitored || 0,
      icon: Users,
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      ringColor: 'ring-blue-500',
      subtitle: `${stats.activeAlertsCount || 0} active alert(s)`,
      isActive: activeFilter === '',
    },
    {
      id: 'action_required',
      filterValue: 'action_required',
      label: 'Action Required',
      value: actionRequiredCount,
      icon: AlertOctagon,
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      ringColor: 'ring-red-500',
      subtitle: actionBreakdown,
      isActive: activeFilter === 'action_required',
      urgent: actionRequiredCount > 0,
    },
    {
      id: 'watch_list',
      filterValue: 'watch_list',
      label: 'Watch List',
      value: watchListCount,
      icon: Eye,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      ringColor: 'ring-amber-500',
      subtitle: watchBreakdown,
      isActive: activeFilter === 'watch_list',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
      {/* Clickable metric cards */}
      {cards.map(card => {
        const Icon = card.icon;
        return (
          <button
            key={card.id}
            onClick={() => handleClick(card.filterValue)}
            className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 text-left transition-all duration-200 hover:shadow-md cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${card.ringColor} ${
              card.isActive && card.filterValue !== '' ? `ring-2 ${card.ringColor}` : ''
            }`}
          >
            <div className="flex items-center">
              <div className={`w-9 h-9 sm:w-12 sm:h-12 ${card.iconBg} rounded-full flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 sm:w-6 sm:h-6 ${card.iconColor}`} />
              </div>
              <div className="ml-2 sm:ml-4 min-w-0">
                <p className={`text-lg sm:text-2xl font-bold ${card.urgent ? 'text-red-600' : 'text-gray-900'}`}>
                  {card.value}
                </p>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{card.label}</p>
              </div>
            </div>
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 truncate">{card.subtitle}</p>
            </div>
          </button>
        );
      })}

      {/* Compliance Rate card (not clickable) */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-center">
          <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
          </div>
          <div className="ml-2 sm:ml-4 min-w-0">
            <p className="text-lg sm:text-2xl font-bold text-gray-900">
              {stats.complianceRate || 0}%
            </p>
            <p className="text-xs sm:text-sm text-gray-600 truncate">Compliance Rate</p>
          </div>
        </div>
        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-100">
          <p className="text-xs text-gray-500 truncate">
            {stats.legallyCompliantCount ?? stats.totalMonitored ?? 0} of {stats.totalMonitored || 0} within limit
          </p>
        </div>
      </div>
    </div>
  );
};

export default RestDayStatsCards;
